import { getCurrentFirebaseUser } from './client'
import { getHanaspeakApiBaseUrl } from './functions-api'

export type BookPdfSlot = 'bookPdf' | 'workbookPdf'

export interface BookPdfReaderPage {
  readonly height: number
  readonly page: number
  readonly width: number
}

export interface BookPdfReaderSession {
  readonly expiresAt: number
  readonly pageCount: number
  readonly pages: readonly BookPdfReaderPage[]
  readonly revision: string
  readonly sessionToken: string
}

interface BookPdfReaderApiPayload {
  readonly error?: unknown
  readonly errorCode?: unknown
}

export class BookPdfReaderApiError extends Error {
  readonly errorCode: string | null
  readonly isSessionFailure: boolean
  readonly status: number

  constructor(message: string, status: number, errorCode: string | null = null) {
    super(message)
    this.name = 'BookPdfReaderApiError'
    this.errorCode = errorCode
    this.isSessionFailure = isPdfSessionFailureStatus(status, errorCode)
    this.status = status
  }
}

function requireReaderUser() {
  const user = getCurrentFirebaseUser()
  if (!user) throw new Error('Not authorized')
  return user
}

async function getReaderIdToken(forceRefresh = false) {
  const user = requireReaderUser()
  return user.getIdToken(forceRefresh)
}

async function readApiPayload(response: Response) {
  return response.json().catch(() => null) as Promise<unknown>
}

function isAuthRetryStatus(status: number) {
  return status === 401 || status === 403
}

function getApiPayload(payload: unknown): BookPdfReaderApiPayload | null {
  return payload && typeof payload === 'object' ? payload as BookPdfReaderApiPayload : null
}

function getApiErrorCode(payload: unknown) {
  const errorCode = getApiPayload(payload)?.errorCode
  return typeof errorCode === 'string' && errorCode.trim() ? errorCode.trim() : null
}

function apiErrorMessage(payload: unknown, fallback: string, status: number) {
  const message = getApiPayload(payload)?.error
  if (typeof message === 'string' && message.trim()) {
    return message
  }

  if (status === 401 || status === 403) {
    return 'The protected PDF reader session has expired.'
  }

  return fallback
}

function isPdfSessionFailureStatus(status: number, errorCode: string | null) {
  if (status !== 401 && status !== 403) {
    return false
  }

  if (!errorCode) {
    return true
  }

  return errorCode === 'PDF_SESSION_REQUIRED' ||
    errorCode === 'PDF_SESSION_INVALID' ||
    errorCode === 'PDF_SESSION_EXPIRED' ||
    errorCode === 'PDF_SESSION_STALE_REVISION'
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0
}

function normalizeSession(payload: unknown): BookPdfReaderSession {
  const candidate = payload as Partial<BookPdfReaderSession> | null
  const pages = Array.isArray(candidate?.pages) ? candidate.pages : []
  const normalizedPages = pages.map((page, index) => {
    const candidatePage = page as Partial<BookPdfReaderPage> | null
    if (
      !isPositiveInteger(candidatePage?.page) ||
      candidatePage.page !== index + 1 ||
      !isPositiveInteger(candidatePage.width) ||
      !isPositiveInteger(candidatePage.height)
    ) {
      throw new Error('Book PDF reader returned an invalid page manifest.')
    }
    return {
      height: candidatePage.height,
      page: candidatePage.page,
      width: candidatePage.width,
    }
  })

  if (
    typeof candidate?.sessionToken !== 'string' || !candidate.sessionToken ||
    typeof candidate?.revision !== 'string' || !candidate.revision ||
    !isPositiveInteger(candidate.expiresAt) ||
    !isPositiveInteger(candidate.pageCount) ||
    normalizedPages.length !== candidate.pageCount
  ) {
    throw new Error('Book PDF reader returned an invalid session.')
  }

  return {
    expiresAt: candidate.expiresAt,
    pageCount: candidate.pageCount,
    pages: normalizedPages,
    revision: candidate.revision,
    sessionToken: candidate.sessionToken,
  }
}

export async function requestBookPdfReaderSession({
  bookId,
  slot,
}: {
  readonly bookId: string
  readonly slot: BookPdfSlot
}): Promise<BookPdfReaderSession> {
  const createRequest = async (forceRefresh: boolean) => {
    const idToken = await getReaderIdToken(forceRefresh)
    return fetch(`${getHanaspeakApiBaseUrl()}/learning/books/pdf/session`, {
      body: JSON.stringify({ bookId, slot }),
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
  }
  let response = await createRequest(false)
  let payload = await readApiPayload(response)
  if (!response.ok && isAuthRetryStatus(response.status)) {
    response = await createRequest(true)
    payload = await readApiPayload(response)
  }

  if (!response.ok) {
    throw new BookPdfReaderApiError(
      apiErrorMessage(payload, 'Book PDF reader session could not be created.', response.status),
      response.status,
      getApiErrorCode(payload),
    )
  }

  return normalizeSession(payload)
}

export async function fetchBookPdfReaderPage({
  page,
  sessionToken,
}: {
  readonly page: number
  readonly sessionToken: string
}): Promise<Blob> {
  if (!isPositiveInteger(page)) throw new Error('Book PDF page is invalid.')
  if (!sessionToken.trim()) throw new Error('Book PDF session is required.')

  const query = new URLSearchParams({ page: String(page) })
  const createRequest = async (forceRefresh: boolean) => {
    const idToken = await getReaderIdToken(forceRefresh)
    return fetch(`${getHanaspeakApiBaseUrl()}/learning/books/pdf/page?${query.toString()}`, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'X-Hanaspeak-Pdf-Session': sessionToken,
      },
      method: 'GET',
    })
  }
  let response = await createRequest(false)

  if (!response.ok) {
    let payload = await readApiPayload(response)
    if (isAuthRetryStatus(response.status)) {
      response = await createRequest(true)
      if (response.ok) {
        const contentType = response.headers.get('content-type')?.split(';', 1)[0].trim().toLowerCase()
        if (contentType !== 'image/png') throw new Error('Book PDF reader returned an invalid page.')
        return response.blob()
      }
      payload = await readApiPayload(response)
    }
    throw new BookPdfReaderApiError(
      apiErrorMessage(payload, 'Book PDF page could not be loaded.', response.status),
      response.status,
      getApiErrorCode(payload),
    )
  }

  const contentType = response.headers.get('content-type')?.split(';', 1)[0].trim().toLowerCase()
  if (contentType !== 'image/png') throw new Error('Book PDF reader returned an invalid page.')
  return response.blob()
}

export const bookPdfReaderSource = {
  fetchPage: fetchBookPdfReaderPage,
  openSession: requestBookPdfReaderSession,
}
